import { useRef } from "react"
import { useTranslation } from "react-i18next"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Field, FieldError, FieldGroup, FieldLabel } from "@/components/ui/field"
import { Input } from "@/components/ui/input"
import { ImagePlus } from "lucide-react"

export const GlassesMetadataCard = ({
  coverPreviewUrl,
  isLocked,
  name,
  onCoverChange,
  onNameChange,
  showValidation,
}: {
  coverPreviewUrl: string
  isLocked?: boolean
  name: string
  onCoverChange: (file: File) => void
  onNameChange: (value: string) => void
  showValidation?: boolean
}) => {
  const { t } = useTranslation()
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  return (
    <Card data-testid="glasses-metadata-card">
      <CardHeader>
        <CardTitle>{t("glassesInfoPage.productCardInfo")}</CardTitle>
      </CardHeader>
      <CardContent className="grid items-start gap-6 lg:grid-cols-[1fr_220px]">
        <FieldGroup>
          <Field data-invalid={(showValidation && !name.trim()) || undefined}>
            <FieldLabel htmlFor="glasses-name">Product Name</FieldLabel>
            <Input
              data-testid="glasses-name-input"
              id="glasses-name"
              placeholder="Enter product name"
              value={name}
              aria-invalid={(showValidation && !name.trim()) || undefined}
              disabled={isLocked}
              onChange={(event) => onNameChange(event.target.value)}
            />
            {showValidation && !name.trim() ? (
              <FieldError>Product name is required</FieldError>
            ) : null}
          </Field>
        </FieldGroup>

        <div>
          <input
            ref={fileInputRef}
            accept="image/png,image/jpeg,image/webp"
            className="hidden"
            disabled={isLocked}
            type="file"
            data-testid="glasses-cover-file-input"
            onChange={(event) => {
              const file = event.target.files?.[0]
              if (file) onCoverChange(file)
              event.target.value = ""
            }}
          />
          <button
            data-testid="glasses-cover-upload"
            className="group flex aspect-square w-full items-center justify-center overflow-hidden rounded-xl border border-dashed border-border bg-muted/20 transition hover:border-vto-accent/40 hover:bg-muted/40 disabled:cursor-not-allowed disabled:opacity-60"
            disabled={isLocked}
            onClick={() => fileInputRef.current?.click()}
            type="button"
          >
            {coverPreviewUrl ? (
              <img
                src={coverPreviewUrl}
                alt="Product"
                className="h-full w-full object-contain p-2"
              />
            ) : (
              <div className="flex flex-col items-center gap-2 px-4 text-center">
                <ImagePlus className="h-5 w-5 text-muted-foreground" />
                <p className="text-sm font-medium">Upload Image</p>
                <p className="text-xs leading-snug text-muted-foreground">
                  PNG, JPG, WebP
                  <br />
                  up to 10MB
                </p>
              </div>
            )}
          </button>
        </div>
      </CardContent>
    </Card>
  )
}
